import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
  brand?: string;
  stock?: number;
}

export default function CartPage() {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    loadCart();
    
    // Escutar atualizações do carrinho
    const handleCartUpdate = () => {
      loadCart();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  const loadCart = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  };

  const updateQuantity = (id: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    
    const updatedItems = cartItems.map(item =>
      item.id === id ? { ...item, quantity: newQuantity } : item
    );
    
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify(updatedItems));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (id: number) => {
    const updatedItems = cartItems.filter(item => item.id !== id);
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify(updatedItems));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal > 500 ? 0 : 49.99;
  const total = subtotal + shipping;

  return (
    <div className="min-h-screen bg-gray-900">
      <Navbar />
      <main className="pt-20">
        <div className="bg-gradient-to-r from-neon-pink via-purple-600 to-neon-cyan text-white py-12 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-900/50"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h1 className="text-4xl font-black mb-2 animate-glow">Carrinho de Compras</h1>
            <p className="text-lg text-gray-100">Revise seus produtos antes de finalizar</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {cartItems.length === 0 ? (
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-12 text-center">
              <i className="ri-shopping-cart-line text-6xl text-transparent bg-clip-text bg-gradient-to-r from-neon-pink to-neon-cyan mb-4"></i>
              <h2 className="text-2xl font-bold text-white mb-2">Seu carrinho está vazio</h2>
              <p className="text-gray-400 mb-6">Adicione produtos para continuar comprando</p>
              <a href="/catalogo" className="inline-block bg-gradient-to-r from-neon-pink via-purple-600 to-neon-cyan hover:shadow-neon-pink text-white px-8 py-3 rounded-lg font-black transition-all whitespace-nowrap cursor-pointer">
                Ver Catálogo
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
                    <h2 className="text-xl font-black text-white flex items-center gap-2">
                      <i className="ri-shopping-bag-3-line text-neon-cyan"></i>
                      Produtos ({cartItems.length})
                    </h2>
                  </div>

                  <div className="divide-y divide-gray-700">
                    {cartItems.map((item) => (
                      <div key={item.id} className="p-6 flex gap-4 hover:bg-gray-750 transition-colors">
                        <div className="w-24 h-24 flex-shrink-0 bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div className="flex-1">
                          {item.brand && <p className="text-xs text-neon-cyan mb-1 font-semibold">{item.brand}</p>}
                          <h3 className="font-bold text-white mb-2">{item.name}</h3>
                          <p className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-neon-pink to-neon-cyan">
                            R$ {item.price.toFixed(2).replace('.', ',')}
                          </p>
                        </div>

                        <div className="flex flex-col items-end justify-between">
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-red-500 hover:text-red-400 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xl"></i>
                          </button>

                          <div className="flex items-center border border-gray-700 rounded-lg bg-gray-900">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              className="px-3 py-1 hover:bg-gray-800 transition-colors cursor-pointer text-white"
                            >
                              <i className="ri-subtract-line"></i>
                            </button>
                            <span className="px-4 py-1 border-x border-gray-700 min-w-[3rem] text-center text-white font-semibold">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="px-3 py-1 hover:bg-gray-800 transition-colors cursor-pointer text-white"
                            >
                              <i className="ri-add-line"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-lg p-6 sticky top-24">
                  <h2 className="text-xl font-black text-white mb-6 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-neon-cyan"></i>
                    Resumo do Pedido
                  </h2>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-gray-400">
                      <span>Subtotal</span>
                      <span className="text-white font-semibold">R$ {subtotal.toFixed(2).replace('.', ',')}</span>
                    </div>
                    <div className="flex justify-between text-gray-400">
                      <span>Frete</span>
                      <span className={shipping === 0 ? 'text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-green-600 font-black' : 'text-white font-semibold'}>
                        {shipping === 0 ? 'GRÁTIS' : `R$ ${shipping.toFixed(2).replace('.', ',')}`}
                      </span>
                    </div>
                    {subtotal < 500 && (
                      <p className="text-xs text-gray-400 bg-gray-900 border border-gray-700 p-3 rounded-lg">
                        <i className="ri-truck-line text-neon-cyan mr-1"></i>
                        Frete grátis para compras acima de R$ 500,00
                      </p>
                    )}
                  </div>

                  <div className="border-t border-gray-700 pt-4 mb-6">
                    <div className="flex justify-between text-lg font-black">
                      <span className="text-white">Total</span>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-pink to-neon-cyan text-2xl">R$ {total.toFixed(2).replace('.', ',')}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">em até 12x sem juros</p>
                  </div>

                  <a
                    href="/checkout"
                    className="block w-full bg-gradient-to-r from-neon-pink via-purple-600 to-neon-cyan hover:shadow-neon-pink text-white py-4 rounded-lg font-black text-center transition-all whitespace-nowrap cursor-pointer mb-3"
                  >
                    <i className="ri-secure-payment-line mr-2"></i>
                    Finalizar Compra
                  </a>

                  <a
                    href="/catalogo"
                    className="block w-full text-center text-transparent bg-clip-text bg-gradient-to-r from-neon-pink to-neon-cyan hover:from-neon-cyan hover:to-neon-pink py-3 font-black cursor-pointer transition-all"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Continuar Comprando
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
